import { calculateAbsoluteDateFromRelativeConfig } from "@common/relativeDateCalculation";
import { RecurringIntervalType } from "../AnnualWheel.types";
import { ContractBasicInfo, maxNotificationPeriodInDays, RelativeDateConfigModel, RelativeDateReference } from "../RelativeDate.types";

export const defaultActivityNotificationPeriodInDays = 14;
export const defaultActivityInterval = 12;

export interface ActivityScheduleValues {
    startDate: Date | null;
    endDate: Date | null;
    displayEndDate: boolean;
    // Null for a one-time activity, undefined while a recurring one has no interval typed in.
    interval: number | null | undefined;
    recurrenceIntervalType: RecurringIntervalType | undefined;
    notificationPeriodInDays: number | undefined;
    useRelativeStartDate: boolean;
    useRelativeEndDate: boolean;
    startDateRelativeConfig: RelativeDateConfigModel | undefined;
    endDateRelativeConfig: RelativeDateConfigModel | undefined;
    // The dates a relative start or end date counts from.
    contractInfo: ContractBasicInfo | undefined;
    hasRelativeDateFromTemplateButCantDisplay: boolean;
}

export class ActivityScheduleModel implements ActivityScheduleValues {
    readonly startDate: Date | null;
    readonly endDate: Date | null;
    readonly displayEndDate: boolean;
    readonly interval: number | null | undefined;
    readonly recurrenceIntervalType: RecurringIntervalType | undefined;
    readonly notificationPeriodInDays: number | undefined;
    readonly useRelativeStartDate: boolean;
    readonly useRelativeEndDate: boolean;
    readonly startDateRelativeConfig: RelativeDateConfigModel | undefined;
    readonly endDateRelativeConfig: RelativeDateConfigModel | undefined;
    readonly contractInfo: ContractBasicInfo | undefined;
    readonly hasRelativeDateFromTemplateButCantDisplay: boolean;

    constructor(values: ActivityScheduleValues) {
        this.startDate = values.startDate ? new Date(values.startDate) : null;
        this.endDate = values.endDate ? new Date(values.endDate) : null;
        this.displayEndDate = values.displayEndDate;
        this.interval = values.interval;
        this.recurrenceIntervalType = values.recurrenceIntervalType;
        this.notificationPeriodInDays = values.notificationPeriodInDays;
        this.useRelativeStartDate = values.useRelativeStartDate;
        this.useRelativeEndDate = values.useRelativeEndDate;
        this.startDateRelativeConfig = relativeToAvailableContractDate(values.startDateRelativeConfig, values.contractInfo);
        this.endDateRelativeConfig = relativeToAvailableContractDate(values.endDateRelativeConfig, values.contractInfo);
        this.contractInfo = values.contractInfo;
        this.hasRelativeDateFromTemplateButCantDisplay = values.hasRelativeDateFromTemplateButCantDisplay;
    }

    static create(values: Partial<ActivityScheduleValues>) {
        return new ActivityScheduleModel({
            startDate: null,
            endDate: null,
            displayEndDate: false,
            interval: defaultActivityInterval,
            recurrenceIntervalType: RecurringIntervalType.Month,
            notificationPeriodInDays: defaultActivityNotificationPeriodInDays,
            useRelativeStartDate: false,
            useRelativeEndDate: false,
            startDateRelativeConfig: undefined,
            endDateRelativeConfig: undefined,
            contractInfo: undefined,
            hasRelativeDateFromTemplateButCantDisplay: false,
            ...values,
        });
    }

    withUpdates(updates: Partial<ActivityScheduleValues>) {
        return new ActivityScheduleModel({ ...this, ...updates });
    }

    isRecurring() {
        return this.interval !== null;
    }

    isInvalid() {
        return this.isIntervalInvalid() || this.isStartDateEmpty() || this.isNotificationPeriodInvalid() || this.isEndDateInvalid();
    }

    isEndDateInvalid() {
        if (!this.displayEndDate) {
            return false;
        }

        const startDate = this.calculateAbsoluteStartDate();
        const endDate = this.calculateAbsoluteEndDate();
        if (!startDate || !endDate) {
            return false;
        }

        return endDate < startDate;
    }

    isStartDateEmpty() {
        if (this.useRelativeStartDate) {
            return !this.startDateRelativeConfig;
        }

        return this.startDate === null;
    }

    isNotificationPeriodInvalid() {
        return (
            this.notificationPeriodInDays === undefined ||
            this.notificationPeriodInDays === null ||
            this.notificationPeriodInDays < 1 ||
            this.notificationPeriodInDays > maxNotificationPeriodInDays
        );
    }

    isIntervalInvalid() {
        if (!this.isRecurring()) {
            return false;
        }

        return this.interval === undefined || this.interval! < 1;
    }

    calculateAbsoluteStartDate(): Date | null {
        if (!this.useRelativeStartDate || !this.startDateRelativeConfig) {
            return this.startDate;
        }

        return calculateAbsoluteDateFromRelativeConfig(this.startDateRelativeConfig, this.contractInfo?.startDate, this.contractInfo?.endDate);
    }

    calculateAbsoluteEndDate(): Date | null {
        if (!this.useRelativeEndDate || !this.endDateRelativeConfig) {
            return this.endDate;
        }

        return calculateAbsoluteDateFromRelativeConfig(this.endDateRelativeConfig, this.contractInfo?.startDate, this.contractInfo?.endDate);
    }
}

// A contract may lack a start or an end date, so a date relative to the missing one counts from the other instead.
function relativeToAvailableContractDate(config: RelativeDateConfigModel | undefined, contractInfo: ContractBasicInfo | undefined) {
    if (config?.relativeTo === RelativeDateReference.ContractStart && !contractInfo?.startDate) {
        return { ...config, relativeTo: RelativeDateReference.ContractEnd };
    }
    if (config?.relativeTo === RelativeDateReference.ContractEnd && !contractInfo?.endDate) {
        return { ...config, relativeTo: RelativeDateReference.ContractStart };
    }

    return config;
}
